import { VALHALLA_URL } from './providers'
import { decodePolyline } from './polyline'
import { cumulativeMeters, destination, type LngLat, type LngLatEle } from './geo'
import type { Instruction, Segment, Sport } from './models'

export interface RouteResult {
  /** geometry with elevation filled in later by attachElevation (0 until then) */
  coords: LngLatEle[]
  distanceM: number
  /** router travel time; UI recomputes with the fitness model */
  durationS: number
  instructions: Instruction[]
}

const COSTING: Record<Sport, { costing: string; options?: Record<string, unknown> }> = {
  hike: { costing: 'pedestrian', options: { pedestrian: { max_hiking_difficulty: 6 } } },
  run: { costing: 'pedestrian', options: { pedestrian: { walking_speed: 9 } } },
  bike: { costing: 'bicycle', options: { bicycle: { bicycle_type: 'Hybrid', use_roads: 0.4 } } },
  mtb: { costing: 'bicycle', options: { bicycle: { bicycle_type: 'Mountain', use_roads: 0.2, use_hills: 0.7 } } },
  road: { costing: 'bicycle', options: { bicycle: { bicycle_type: 'Road', use_roads: 0.7 } } },
  gravel: { costing: 'bicycle', options: { bicycle: { bicycle_type: 'Cross', use_roads: 0.25 } } },
}

interface ValhallaManeuver {
  instruction: string
  type: number
  begin_shape_index: number
  length: number
}

interface ValhallaLeg {
  shape: string
  maneuvers: ValhallaManeuver[]
}

export class RoutingError extends Error {}

/** Compute a route through the waypoints with the FOSSGIS Valhalla server. */
export async function computeRoute(waypoints: LngLat[], sport: Sport, signal?: AbortSignal): Promise<RouteResult> {
  if (waypoints.length < 2) throw new RoutingError('need at least two waypoints')
  const { costing, options } = COSTING[sport]
  const body = {
    locations: waypoints.map((w, i) => ({
      lon: w[0],
      lat: w[1],
      type: i === 0 || i === waypoints.length - 1 ? 'break' : 'through',
    })),
    costing,
    costing_options: options,
    directions_options: { units: 'kilometers', language: 'en-US' },
  }
  const res = await fetch(`${VALHALLA_URL}/route`, { method: 'POST', body: JSON.stringify(body), signal })
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j: { error?: string }) => j.error)
      .catch(() => undefined)
    throw new RoutingError(detail ?? `routing failed (${res.status})`)
  }
  const data = (await res.json()) as {
    trip: { legs: ValhallaLeg[]; summary: { length: number; time: number } }
  }

  const coords: LngLatEle[] = []
  const instructions: Instruction[] = []
  let legStartM = 0
  for (const leg of data.trip.legs) {
    const shape = decodePolyline(leg.shape, 6)
    const startIdx = coords.length ? 1 : 0 // legs share their boundary vertex
    for (let i = startIdx; i < shape.length; i++) coords.push([shape[i][0], shape[i][1], 0])
    const cum = cumulativeMeters(shape)
    for (const m of leg.maneuvers) {
      instructions.push({
        text: m.instruction,
        atM: legStartM + cum[Math.min(m.begin_shape_index, cum.length - 1)],
        sign: m.type,
      })
    }
    legStartM += cum[cum.length - 1] ?? 0
  }
  return {
    coords,
    distanceM: Math.round(data.trip.summary.length * 1000),
    durationS: data.trip.summary.time,
    instructions,
  }
}

/** Komoot-style round trip: route through points on a circle from the start. */
export async function computeRoundTrip(
  start: LngLat,
  targetDistM: number,
  sport: Sport,
  bearingSeed: number,
  signal?: AbortSignal,
): Promise<{ result: RouteResult; via: LngLat[] }> {
  // circumference ≈ target distance → radius; real routes wiggle, so shrink a bit
  const r = (targetDistM / (2 * Math.PI)) * 0.85
  const center = destination(start, r, bearingSeed)
  const via: LngLat[] = [start]
  for (const angle of [120, 240]) {
    via.push(destination(center, r, (bearingSeed + 180 + angle) % 360))
  }
  via.push(start)
  const result = await computeRoute(via, sport, signal)
  return { result, via: via.slice(1, -1) }
}

const SURFACE_LABELS: Record<string, string> = {
  paved_smooth: 'Paved',
  paved: 'Paved',
  paved_rough: 'Rough paved',
  compacted: 'Compacted gravel',
  gravel: 'Gravel',
  dirt: 'Dirt',
  path: 'Natural',
  impassable: 'Impassable',
}

const USE_LABELS: Record<string, string> = {
  track: 'Track',
  footway: 'Path',
  path: 'Path',
  pedestrian: 'Pedestrian zone',
  cycleway: 'Cycleway',
  mountain_bike: 'Singletrack',
  steps: 'Steps',
  living_street: 'Street',
  residential: 'Street',
  service_road: 'Street',
  service_other: 'Street',
  alley: 'Street',
  driveway: 'Street',
  culdesac: 'Street',
  road: 'Road',
  ramp: 'Road',
  turn_channel: 'Road',
  bridleway: 'Bridleway',
  ferry: 'Ferry',
}

function mergeSegments(raw: { kind: string; lenM: number }[]): Segment[] {
  const out: Segment[] = []
  let at = 0
  for (const r of raw) {
    const last = out[out.length - 1]
    if (last && last.kind === r.kind) last.toM = at + r.lenM
    else out.push({ kind: r.kind, fromM: at, toM: at + r.lenM })
    at += r.lenM
  }
  return out
}

/**
 * Surface + way-type breakdown for a computed route via Valhalla trace_attributes
 * (edge_walk works because the shape came from the same graph).
 */
export async function fetchRouteExtras(
  coords: readonly LngLatEle[],
  sport: Sport,
  signal?: AbortSignal,
): Promise<{ surfaces: Segment[]; waytypes: Segment[] } | null> {
  const { costing, options } = COSTING[sport]
  const body = {
    shape: coords.map((c) => ({ lon: c[0], lat: c[1] })),
    costing,
    costing_options: options,
    shape_match: 'edge_walk',
    filters: { attributes: ['edge.surface', 'edge.use', 'edge.length'], action: 'include' },
  }
  const res = await fetch(`${VALHALLA_URL}/trace_attributes`, { method: 'POST', body: JSON.stringify(body), signal })
  if (!res.ok) return null
  const data = (await res.json()) as { edges?: { surface?: string; use?: string; length?: number }[] }
  if (!data.edges?.length) return null
  const surf: { kind: string; lenM: number }[] = []
  const way: { kind: string; lenM: number }[] = []
  for (const e of data.edges) {
    const lenM = (e.length ?? 0) * 1000
    if (lenM <= 0) continue
    surf.push({ kind: SURFACE_LABELS[e.surface ?? ''] ?? 'Unknown', lenM })
    way.push({ kind: USE_LABELS[e.use ?? ''] ?? 'Other', lenM })
  }
  return { surfaces: mergeSegments(surf), waytypes: mergeSegments(way) }
}

/** Speed model per sport: [flat km/h, minutes added per 100 m ascent] at average fitness. */
const SPEED: Record<Sport, [number, number]> = {
  hike: [4.4, 10],
  run: [9.5, 8],
  bike: [17, 5.5],
  mtb: [13, 5],
  road: [24, 4],
  gravel: [19, 4.5],
}

export type Fitness = 'casual' | 'average' | 'athletic'
const FITNESS_FACTOR: Record<Fitness, number> = { casual: 0.8, average: 1, athletic: 1.25 }

export function estimateDurationS(sport: Sport, distanceM: number, ascentM: number, fitness: Fitness = 'average'): number {
  const [flatKmh, minPer100m] = SPEED[sport]
  const f = FITNESS_FACTOR[fitness]
  const flatS = (distanceM / 1000 / (flatKmh * f)) * 3600
  const climbS = (ascentM / 100) * minPer100m * 60 / f
  return Math.round(flatS + climbS)
}

export type Difficulty = 'easy' | 'intermediate' | 'expert'

/** Komoot-style grade from effort (distance + climb) — transparent heuristic. */
export function gradeDifficulty(sport: Sport, distanceM: number, ascentM: number): Difficulty {
  const effortKm = distanceM / 1000 + ascentM / 100 // 100 m up ≈ 1 km flat
  const [easyMax, intermediateMax] =
    sport === 'hike' ? [12, 25] : sport === 'run' ? [10, 21] : sport === 'mtb' ? [25, 50] : [40, 80]
  if (effortKm <= easyMax) return 'easy'
  if (effortKm <= intermediateMax) return 'intermediate'
  return 'expert'
}
